<script lang="ts">
  import { Button } from "$lib/components/ui/button/index.js";
  import Layout from "$lib/components/Layout.svelte";
  import Sparkles from "@lucide/svelte/icons/sparkles";
  import Trophy from "@lucide/svelte/icons/trophy";
  import MapPin from "@lucide/svelte/icons/map-pin";
  import ArrowRight from "@lucide/svelte/icons/arrow-right";
  import Calendar from "@lucide/svelte/icons/calendar";
  import Star from "@lucide/svelte/icons/star";
  import Heart from "@lucide/svelte/icons/heart";

  const features = [
    {
      icon: "sparkles",
      title: "Specialized in Asian Beauty",
      description: "Expert in Asian skin tones and features"
    },
    {
      icon: "trophy",
      title: "Award-Winning Artist",
      description: "1st place winner at the 2025 America's Beauty Show"
    },
    {
      icon: "heart",
      title: "Customized for You",
      description: "Tailored looks that enhance your natural beauty"
    },
    {
      icon: "map-pin",
      title: "On-Location Service",
      description: "Serving Chicagoland and surrounding areas"
    }
  ];

  const packages = [
    {
      image: "/photos/portfolio/3/2-thumb.webp",
      title: "Bridal Makeup",
      description: "Timeless looks for your special day"
    },
    {
      image: "/photos/portfolio/7/3-thumb.webp",
      title: "Event Makeup",
      description: "Perfect for galas, parties, and special occasions"
    },
    {
      image: "/photos/portfolio/1/2-thumb.webp",
      title: "Editorial Makeup",
      description: "Creative, polished looks for photoshoots and more"
    },
    {
      image: "/images/lessons-thumb.webp",
      title: "Lessons & Groups",
      description: "Learn techniques for any skill level"
    }
  ];

  const testimonials = [
    {
      stars: 5,
      quote: "She did a fantastic job on my makeup - very professional and talented. I really appreciated her attention to detail and the care she took. The results were absolutely perfect and exceeded my expectations. I would highly recommend her services to anyone.",
      author: "— Mai Nguyen"
    },
    {
      stars: 5,
      quote: "What a wonderful experience from beginning to end! She listened carefully to all my requests and delivered exactly what I envisioned. The makeup stayed flawless all day long without any touch-ups needed. I felt beautiful and confident throughout my special event.",
      author: "— Anh Le"
    },
    {
      stars: 5,
      quote: "The makeup stayed on all night without smudging or fading, even in the heat. Her work is truly exceptional and she has excellent taste in color choices. I received so many compliments on how natural and glowing I looked. I will definitely be recommending her to all my friends.",
      author: "— Nga Pham"
    }
  ];
</script>

<Layout activePage="home" title="Home">
  <!-- Hero Section -->
  <section class="relative overflow-hidden bg-gradient-to-br from-pink-50 via-pink-100 to-rose-100">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
      <div class="grid md:grid-cols-2 gap-12 items-center">
        <!-- Hero Content -->
        <div class="space-y-8 relative z-10 text-center md:text-left">
          <p class="text-primary tracking-widest uppercase text-sm font-medium">Enhancing Your Natural Beauty</p>
          <h1 class="text-5xl lg:text-6xl font-serif text-gray-800 leading-tight">
            Makeup Artistry<br />
            That Celebrates <span class="italic text-primary font-light">You</span>
          </h1>
          <p class="text-gray-600 text-lg max-w-lg leading-relaxed mx-auto md:mx-0">
            Specializing in Asian skin tones and features. Bridal, editorial, and event makeup that brings out your natural beauty.
          </p>
          <div class="flex flex-wrap gap-4 justify-center md:justify-start">
            <Button href="/contact.html" class="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-sm tracking-wide gap-2">
              <Sparkles class="w-4 h-4" />
              BOOK YOUR APPOINTMENT
              <ArrowRight class="w-4 h-4" />
            </Button>
          </div>

          <!-- Award Badge -->
          <div class="flex items-center gap-4 pt-4 justify-center md:justify-start">
            <div class="w-16 h-16 bg-gradient-to-br from-yellow-300 to-amber-400 rounded-full flex items-center justify-center shadow-lg shadow-yellow-400/40">
              <Trophy class="w-7 h-7 text-black" />
            </div>
            <div>
              <p class="font-semibold text-gray-800">1ST PLACE WINNER</p>
              <p class="text-sm text-gray-600">2025 America's Beauty Show Competition<br>(Flourish and Bloom theme)</p>
            </div>
          </div>
        </div>

        <!-- Hero Image -->
        <div class="relative">
          <div class="absolute inset-0 bg-gradient-to-br from-pink-200/50 to-rose-200/50 rounded-full blur-3xl"></div>
          <img
            src="/images/hero.webp"
            alt="Professional Makeup Artistry"
            class="relative w-full max-w-lg mx-auto rounded-3xl shadow-2xl"
          />
        </div>
      </div>
    </div>
  </section>

  <!-- Features Section -->
  <section class="py-16 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-8">
        {#each features as feature}
          <div class="text-center space-y-4">
            <div class="w-20 h-20 mx-auto bg-pink-50 rounded-full flex items-center justify-center">
              {#if feature.icon === "sparkles"}
                <Sparkles class="w-8 h-8 text-pink-500" />
              {:else if feature.icon === "trophy"}
                <Trophy class="w-8 h-8 text-pink-500" />
              {:else if feature.icon === "heart"}
                <Heart class="w-8 h-8 text-pink-500" />
              {:else if feature.icon === "map-pin"}
                <MapPin class="w-8 h-8 text-pink-500" />
              {/if}
            </div>
            <h2 class="font-semibold text-gray-800">{feature.title}</h2>
            <p class="text-sm text-gray-600">{feature.description}</p>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- About Section -->
  <section id="about" class="py-20 bg-gradient-to-b from-white to-pink-50/50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-16 items-center">
        <!-- About Image -->
        <div class="relative">
          <div class="absolute -inset-4 bg-gradient-to-br from-pink-200 to-rose-200 rounded-3xl blur-lg opacity-50"></div>
          <img
            src="/images/about.webp"
            alt="Makeup Products"
            class="relative rounded-2xl shadow-xl w-full"
          />
        </div>

        <!-- About Content -->
        <div class="space-y-6">
          <p class="text-primary tracking-widest uppercase text-sm font-medium">About Me</p>
          <h2 class="text-4xl font-serif text-gray-800">Your Beauty, My Passion</h2>
          <div class="space-y-4 text-gray-600 leading-relaxed">
            <p>
              I am a licensed cosmetologist with a passion for enhancing natural beauty through thoughtful, personalized artistry.
            </p>
            <p>
              From bridal and editorial to special events, I create looks that not only make you feel beautiful but also feel like you.
            </p>
            <p>
              My streamlined process begins with a consultation to understand your vision, followed by customized application using premium products to perfect your look.
            </p>
          </div>
          <Button href="/portfolio.html" class="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-sm tracking-wide gap-2">
            <ArrowRight class="w-4 h-4" />
            SEE MY PORTFOLIO
            <ArrowRight class="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  </section>

  <!-- Packages & Portfolio Section -->
  <section id="packages" class="py-20 bg-pink-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
        <div>
          <p class="text-primary tracking-widest uppercase text-sm font-medium mb-2">Packages & Portfolio</p>
          <h2 class="text-4xl font-serif text-gray-800">Explore Services & See My Work</h2>
        </div>
        <a href="/packages.html" class="text-primary font-medium hover:underline flex items-center gap-2">
          VIEW ALL PACKAGES <ArrowRight class="w-4 h-4" />
        </a>
      </div>

      <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {#each packages as pkg}
          <div class="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow group">
            <div class="aspect-[4/5] overflow-hidden">
              <img
                src={pkg.image}
                alt={pkg.title}
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div class="p-5">
              <div class="flex justify-between items-start">
                <div>
                  <h3 class="font-semibold text-gray-800 mb-1">{pkg.title}</h3>
                  <p class="text-sm text-gray-600">{pkg.description}</p>
                </div>
                <ArrowRight class="w-5 h-5 text-primary" />
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Testimonials Section -->
  <section id="testimonials" class="py-20 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-12">
        <p class="text-primary tracking-widest uppercase text-sm font-medium mb-2">Kind Words</p>
        <h2 class="text-4xl font-serif text-gray-800">Client Love</h2>
      </div>

      <div class="grid md:grid-cols-3 gap-8">
        {#each testimonials as testimonial}
          <div class="bg-pink-50/50 rounded-2xl p-8 text-center space-y-4">
            <div class="flex justify-center gap-1 text-primary">
              {#each Array(testimonial.stars) as _}
                <Star class="w-5 h-5 fill-current" />
              {/each}
            </div>
            <p class="text-gray-600 italic leading-relaxed">"{testimonial.quote}"</p>
            <p class="font-medium text-gray-800">{testimonial.author}</p>
          </div>
        {/each}
      </div>

      <!-- Pagination Dots -->
      <div class="flex justify-center gap-2 mt-8">
        <span class="w-2 h-2 rounded-full bg-primary"></span>
        <span class="w-2 h-2 rounded-full bg-pink-200"></span>
        <span class="w-2 h-2 rounded-full bg-pink-200"></span>
        <span class="w-2 h-2 rounded-full bg-pink-200"></span>
      </div>
    </div>
  </section>

  <!-- CTA Section -->
  <section id="contact" class="py-16 bg-gradient-to-r from-pink-100 via-pink-50 to-rose-100">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="bg-white rounded-3xl shadow-xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
        <img
          src="/images/logo_photo_transparent.webp"
          alt="Chicago Beauty Studio"
          class="w-24 h-24 rounded-full bg-gradient-to-br from-pink-100 to-rose-100"
        />
        <div class="flex-1 text-center md:text-left">
          <h2 class="text-2xl font-serif text-gray-800 mb-2">Ready to feel your most beautiful?</h2>
          <p class="text-gray-600">Let's create a look that's uniquely you.</p>
        </div>
        <Button href="/contact.html" class="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-sm tracking-wide whitespace-nowrap gap-2">
          CONTACT ME TO BOOK
          <Calendar class="w-4 h-4" />
        </Button>
      </div>
    </div>
  </section>
</Layout>
